import React, { useState, useEffect } from 'react';
import { pillarsAPI } from '../services/api';
import { Pillar } from '../types';
import PillarCard from '../components/PillarCard';
import CreatePillarModal from '../components/CreatePillarModal';
// @ts-ignore
import PillarSidePanel from '../components/PillarSidePanel';
import ElementPreviewPanel from '../components/ElementPreviewPanel';
import '../styles/Pillars.css';

const Pillars: React.FC = () => {
  console.log('📊 Pillars component rendering');
  
  const [pillars, setPillars] = useState<Pillar[]>([]);
  const [filteredPillars, setFilteredPillars] = useState<Pillar[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name-asc' | 'name-desc' | 'date-created' | 'date-created-oldest' | 'level-asc' | 'level-desc'>('level-asc');
  
  // New filter states
  const [selectedL1Pillar, setSelectedL1Pillar] = useState<string>('all');
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  
  // Global tab selection state
  const [globalTabSelection, setGlobalTabSelection] = useState<string>('article');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPillarPanel, setShowPillarPanel] = useState(false);
  const [showElementPreview, setShowElementPreview] = useState(false);
  const [selectedPillar, setSelectedPillar] = useState<Pillar | null>(null);
  const [updatedPillarIds, setUpdatedPillarIds] = useState<Set<string>>(new Set());

  // Get unique L1 pillars and levels for filter options
  const l1Pillars = Array.from(new Set(pillars.map(p => p.L1pillar).filter(Boolean)));
  const levels = Array.from(new Set(pillars.map(p => p.level).filter((level): level is number => level !== undefined))).sort((a, b) => a - b);

  useEffect(() => {
    loadPillars();
  }, []);

  // Monitor pillars array changes for debugging (only log significant changes)
  useEffect(() => {
    if (pillars.length > 0) {
      console.log('🔄 Pillars: Pillars loaded:', {
        total: pillars.length,
        withImageUrl: pillars.filter(p => p.imageUrl).length
      });
    }
  }, [pillars.length]);

  useEffect(() => {
    // Filter and sort pillars
    if (Array.isArray(pillars)) {
      let filtered = pillars.filter(pillar => {
        if (!pillar || !pillar.name) return false;
        
        // Search filter
        const matchesSearch = pillar.name.toLowerCase().includes(searchTerm.toLowerCase());
        
        // L1 Pillar filter
        const matchesL1Pillar = selectedL1Pillar === 'all' || pillar.L1pillar === selectedL1Pillar;
        
        // Level filter
        const matchesLevel = selectedLevel === 'all' || pillar.level?.toString() === selectedLevel;
        
        return matchesSearch && matchesL1Pillar && matchesLevel;
      });

             // Sort the filtered pillars
       filtered = filtered.sort((a, b) => {
         switch (sortBy) {
           case 'name-asc':
             return a.name.localeCompare(b.name);
           case 'name-desc':
             return b.name.localeCompare(a.name);
           case 'date-created':
             // Sort by creation date (newest first)
             const dateA = new Date(a.createdAt || a._id);
             const dateB = new Date(b.createdAt || b._id);
             return dateB.getTime() - dateA.getTime();
           case 'date-created-oldest':
             // Sort by creation date (oldest first)
             const dateAOldest = new Date(a.createdAt || a._id);
             const dateBOldest = new Date(b.createdAt || b._id);
             return dateAOldest.getTime() - dateBOldest.getTime();
           case 'level-asc':
             // Sort by level (ascending)
             const levelA = a.level || 0;
             const levelB = b.level || 0;
             return levelA - levelB;
           case 'level-desc':
             // Sort by level (descending)
             const levelADesc = a.level || 0;
             const levelBDesc = b.level || 0;
             return levelBDesc - levelADesc;
             
           default:
             return 0;
         }
       });

      setFilteredPillars(filtered);
    } else {
      setFilteredPillars([]);
    }
  }, [pillars, searchTerm, sortBy, selectedL1Pillar, selectedLevel]);

  const loadPillars = async () => {
    try {
      setLoading(true);
      const data = await pillarsAPI.getAll();
      console.log('Pillars data received:', data);
      // Ensure data is an array
      if (Array.isArray(data)) {
        setPillars(data);
      } else {
        console.error('API returned non-array data:', data);
        setPillars([]);
      }
    } catch (error) {
      console.error('Failed to load pillars:', error);
      setPillars([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePillarCreated = (newPillar: Pillar) => {
    console.log('New pillar created:', newPillar);
    
    // Refresh all pillars to ensure hierarchy and relationships are properly updated
    const refreshAllPillars = async () => {
      try {
        console.log('🔄 Pillars: Refreshing all pillars after creation...');
        const refreshedPillars = await pillarsAPI.getAll();
        setPillars(refreshedPillars);
        console.log('🔄 Pillars: All pillars refreshed successfully after creation');
        
        // Show success toast
        if (typeof window.showToast === 'function') {
          window.showToast(`Pillar "${newPillar.name}" created successfully!`, 'success');
        }
      } catch (error) {
        console.error('🔄 Pillars: Failed to refresh all pillars after creation:', error);
        // Fallback to adding just the new pillar
        setPillars(prev => {
          const updated = [...prev, newPillar];
          console.log('Updated pillars array (fallback):', updated);
          return updated;
        });
      }
    };
    
    refreshAllPillars();
    setShowCreateModal(false);
  };

  const handleUpdatePillar = (updatedPillar: Pillar) => {
    console.log('🔄 Pillars: Updating pillar:', {
      id: updatedPillar._id,
      name: updatedPillar.name,
      imageUrl: updatedPillar.imageUrl?.substring(0, 50) + '...'
    });
    
    // Refresh all pillars to ensure linked arrays are current
    const refreshAllPillars = async () => {
      try {
        console.log('🔄 Pillars: Refreshing all pillars after update...');
        const refreshedPillars = await pillarsAPI.getAll();
        setPillars(refreshedPillars);
        console.log('🔄 Pillars: All pillars refreshed successfully');
      } catch (error) {
        console.error('🔄 Pillars: Failed to refresh all pillars:', error);
        // Fallback to updating just the specific pillar
        setPillars(prev => {
          const updated = prev.map(p => 
            p._id === updatedPillar._id ? updatedPillar : p
          );
          return updated;
        });
      }
    };
    
    refreshAllPillars();
    
    // Track which pillar was updated for targeted re-rendering
    setUpdatedPillarIds(prev => new Set(Array.from(prev).concat(updatedPillar._id)));
    
    // Clear the updated flag after a short delay to prevent unnecessary re-renders
    setTimeout(() => {
      setUpdatedPillarIds(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(updatedPillar._id);
        return newSet;
      });
    }, 1000);
  };

  const handleDeletePillar = (pillarId: string) => {
    setPillars(prev => prev.filter(p => p._id !== pillarId));
  };

  const handleOpenSidePanel = (pillar: Pillar, field: string) => {
    setSelectedPillar(pillar);
    setShowPillarPanel(true);
  };

  const handleOpenPreviewPanel = (elementIds: string[]) => {
    // Handle preview panel
    console.log('Opening preview for elements:', elementIds);
  };

  if (loading) {
    return (
      <div className="overview">
        <div className="loading">
          <p>Loading pillars...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="overview">
        {/* Header - Similar to Overview page */}
        <div className="overview-header">
          <h1 className="overview-title">Pillars</h1>
          <p className="overview-subtitle">Manage your world's pillars</p>
        </div>

        {/* Header with actions - similar to old overview */}
        <div className="overview-actions">
          <button 
            className="btn btn--green" 
            onClick={() => setShowCreateModal(true)}
          >
            Create New Pillar
          </button>
          <input
            type="text"
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="live-search"
          />
                     <select
             value={sortBy}
             onChange={(e) => setSortBy(e.target.value as 'name-asc' | 'name-desc' | 'date-created' | 'date-created-oldest' | 'level-asc' | 'level-desc')}
             className="sort-select"
           >
             <option value="date-created">Newest First</option>
             <option value="date-created-oldest">Oldest First</option>
             <option value="name-asc">A to Z</option>
             <option value="name-desc">Z to A</option>
                           <option value="level-asc">Level (Top to Bottom)</option>
              <option value="level-desc">Level (Bottom to Top)</option>
           </select>
        </div>

        {/* Filter Section */}
        <div className="filter-section">
          {/* Left side - Filter dropdowns */}
          <div className="filter-left">
            <div className="filter-group">
              <label htmlFor="l1-pillar-filter" className="filter-label">L1 Pillar:</label>
              <select
                id="l1-pillar-filter"
                value={selectedL1Pillar}
                onChange={(e) => setSelectedL1Pillar(e.target.value)}
                className="filter-select"
              >
                <option value="all">All L1 Pillars</option>
                {l1Pillars.map(l1Pillar => (
                  <option key={l1Pillar} value={l1Pillar}>
                    {l1Pillar}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="level-filter" className="filter-label">Pillar Level:</label>
              <select
                id="level-filter"
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Levels</option>
                {levels.map(level => (
                  <option key={level} value={level.toString()}>
                    Level {level}
                  </option>
                ))}
              </select>
            </div>

            <button
              className="btn btn--clear-filters"
              onClick={() => {
                setSelectedL1Pillar('all');
                setSelectedLevel('all');
                setSearchTerm('');
              }}
            >
              Clear Filters
            </button>
          </div>

          {/* Right side - Tab selection */}
          <div className="filter-right">
            <div className="filter-group">
              <label htmlFor="tab-filter" className="filter-label">Default Tab:</label>
              <select
                id="tab-filter"
                value={globalTabSelection}
                onChange={(e) => setGlobalTabSelection(e.target.value)}
                className="filter-select tab-select"
              >
                <option value="article">Article</option>
                <option value="ancestors">Ancestors</option>
                <option value="children">Children</option>
                <option value="elements">Elements</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Card Container - Outside overview div */}
      <div className="card-container" id="results-container">
        {Array.isArray(filteredPillars) && filteredPillars.length > 0 ? (
          filteredPillars.map(pillar => (
            <PillarCard
              key={`${pillar._id}-${pillar.imageUrl || pillar.image || 'default'}-${updatedPillarIds.has(pillar._id) ? 'updated' : 'stable'}`}
              pillar={pillar}
              onUpdate={handleUpdatePillar}
              onDelete={handleDeletePillar}
              onOpenSidePanel={handleOpenSidePanel}
              onOpenPreviewPanel={handleOpenPreviewPanel}
              defaultTab={globalTabSelection}
            />
          ))
        ) : (
          <div className="no-results">
            <p>No pillars found.</p>
          </div>
        )}
      </div>

      {/* Create Pillar Modal */}
      {showCreateModal && (
        <CreatePillarModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onPillarCreated={handlePillarCreated}
        />
      )}

      {/* Pillar Side Panel */}
      {showPillarPanel && selectedPillar && (
        <PillarSidePanel
          isOpen={showPillarPanel}
          pillar={selectedPillar}
          onClose={() => {
            setShowPillarPanel(false);
            setSelectedPillar(null);
            setSelectedPillar(null);
          }}
          onUpdate={handleUpdatePillar}
        />
      )}

      {/* Element Preview Panel */}
      {showElementPreview && (
        <ElementPreviewPanel
          isOpen={showElementPreview}
          onClose={() => setShowElementPreview(false)}
        />
      )}
    </>
  );
};

export default Pillars; 